import { CloseOutlined } from "@ant-design/icons";
import { Button, Input, Modal, Progress } from "antd";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import fixWebmDuration from "webm-duration-fix";
import apiFactory from "../../api";
import { uploadMultipartFile } from "../../api/authentication/storageApi";
import { FILE_STATUS, MESSAGE_STATUS } from "../../config/Constant";
import { useInfoUser } from "../../store/UserStore";
import { formatTimeBySeconds } from "../../utils/formatTime";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../utils/Utils";
import "./style.scss";

const RecordStoreModal = ({
  title,
  duration,
  payload,
  changeName,
  onCancel,
  open,
  uploadFile,
}) => {
  const { languageMap } = useInfoUser();
  const [abortController, setAbortController] = useState(null);
  const [progressState, setProgressState] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const sendFile = async () => {
    setIsLoading(true);
    const requestUuid = payload?.requestUuid || uuidv4();
    const controller = new AbortController();
    setAbortController(controller);

    try {
      if (!uploadFile) return null;

      const fixedBlob = await fixWebmDuration(uploadFile);
      const fileName = `${payload?.name || "recording"}.webm`;
      const videoFile = new File([fixedBlob], fileName, {
        type: "video/webm",
      });

      const resource = await uploadMultipartFile({
        file: videoFile,
        bucket: "conversation",
        signal: controller.signal,
        onProgress: ({ percent }) => {
          setProgressState(percent || 0);
        },
      });

      const sendMessagePayload = {
        ...payload,
        requestUuid,
        conversationId: payload?.conversationId,
        folder: payload?.folder || "CONVERSATION",
        contentType: MESSAGE_STATUS.FILE,
        content: resource?.fileName || fileName,
        status: FILE_STATUS.READY,
        file: {
          mainPath: resource?.location,
          videoImgPreviewPath: resource?.videoImgPreviewPath,
          originalName: resource?.fileName || fileName,
          volume: resource?.fileSize || fixedBlob.size,
          contentType: resource?.contentType || "video/webm",
          duration: duration,
        },
      };

      const rs = await apiFactory.messageApi.sendMessage(sendMessagePayload);

      if (rs?.status === 200) {
        setTimeout(() => {
          onCancel();
          openNotificationSuccess("Your recording save successfully");
        }, 1000);
      } else {
        openNotificationError("Your recording save unsuccessfully");
      }
    } catch (err) {
      if (controller.signal.aborted) {
        return;
      }

      console.error("Record upload failed:", err);
      openNotificationError("Your recording save unsuccessfully");
    } finally {
      setIsLoading(false);
    }
  };

  const getFileSize = (file) => {
    if (!file?.size) return "0 KB";

    const sizeInMB = file.size / (1024 * 1024);
    const sizeInKB = file.size / 1024;

    return sizeInMB >= 1
      ? `${sizeInMB.toFixed(1)} MB`
      : `${sizeInKB.toFixed(1)} KB`;
  };

  const handleDeleteFile = async () => {
    if (uploadFile && abortController) {
      abortController.abort();
      const res = await apiFactory.messageApi.cancelMessage({
        requestUuid: payload?.requestUuid,
      });

      if (res?.status === 200) {
        openNotificationSuccess(
          languageMap?.[res?.message] || "Cancel upload recording successfully",
        );
        setIsLoading(false);
      }
    }
  };

  return (
    <Modal
      width={450}
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      maskClosable={false}
      className="custom-confirm-modal"
    >
      <div className="modal-header">
        <div className="modal-title">{title ?? "Xác nhận"}</div>
        <div className="modal-divider" />
      </div>

      <div className="text-base mb-6">
        Video duration: {formatTimeBySeconds(duration)}
      </div>
      <div className="flex flex-row gap-[10px] text-base mb-6">
        <div>Video name: </div>
        <Input
          disabled={isLoading}
          className="flex-1"
          value={payload?.name}
          onChange={changeName}
        />
      </div>
      {isLoading ? (
        <div key={uploadFile?.uid}>
          <div className="mt-[20px]">
            <div className="flex items-center mt-[10px] justify-between">
              <button onClick={handleDeleteFile}>
                <CloseOutlined />
              </button>
              <div>{getFileSize(uploadFile)}</div>
            </div>
            <Progress percent={progressState || 0} className="w-[100%]" />
          </div>
        </div>
      ) : (
        <div className="flex justify-end gap-2">
          <Button
            className="!bg-[#ffffff] !text-black hover:!bg-[#d9d9d9] font-medium"
            onClick={onCancel}
          >
            {languageMap?.["modal.cancel"] ?? "Không"}
          </Button>
          <Button
            className="!bg-[#ff4d4f] !text-white hover:!bg-[#ff7875] font-medium"
            onClick={sendFile}
          >
            {languageMap?.["modal.ok"] ?? "Xóa"}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export { RecordStoreModal };
